import {
  CreateCognitoUserMutation,
  CreateCognitoUserMutationVariables,
  CreateCognitoUserDocument,
  RecordPasswordChangeLogMutation,
  RecordPasswordChangeLogMutationVariables,
  RecordPasswordChangeLogDocument,
  GetCognitoUserCountQuery,
  GetCognitoUserCountDocument,
  UpdateCognitoUserMutation,
  UpdateCognitoUserMutationVariables,
  UpdateCognitoUserDocument,
  OperationInput,
  ListCognitoUserQuery,
  ListCognitoUserQueryVariables,
  ListCognitoUserDocument,
  CognitoUserCountInput,
  CognitoUserSameEmailCountInput,
  CognitoUserListInput,
  GetCognitoUserQuery,
  GetCognitoUserDocument,
  CognitoUserCommonInput,
  DeleteCognitoUserMutation,
  DeleteCognitoUserMutationVariables,
  CognitoUserUpdateInput,
  DeleteCognitoUserDocument,
  CognitoUserCreateInput,
  CognitoUserPasswordUpdateInput,
  UpdateCognitoUserPasswordMutationVariables,
  UpdateCognitoUserPasswordMutation,
  UpdateCognitoUserPasswordDocument,
  GetCognitoUserSameEmailCountQuery,
  GetCognitoUserSameEmailCountDocument,
} from "@/generated/graphql";
import { ApolloQueryResult, FetchResult } from "@apollo/client/core";
import { generateApolloClient } from "../apolloClientUtil";

/**
 * オペレータ登録をする
 * @param cognitoUserCreateInput オペレータ登録情報
 * @returns 登録結果
 */
export async function createCognitoUser(
  cognitoUserCreateInput: CognitoUserCreateInput
): Promise<CreateCognitoUserMutation | null | undefined> {
  try {
    //graphql接続
    const apolloClient = await generateApolloClient();
    const result: FetchResult<CreateCognitoUserMutation> =
      await apolloClient.mutate<
        CreateCognitoUserMutation,
        CreateCognitoUserMutationVariables
      >({
        mutation: CreateCognitoUserDocument,
        variables: {
          input: cognitoUserCreateInput,
        },
      });
    return result.data;
  } catch (err: unknown) {
    console.error(err);
    if (err instanceof Error) {
      err.message = "オペレータ登録に失敗しました。";
      throw err;
    } else {
      throw new Error("オペレータ登録に失敗しました。（Unknown error）");
    }
  }
}

/**
 * パスワード変更ログを記録する
 * @param operationInput 操作履歴情報
 * @returns 記録結果
 */
export async function recordPasswordChangeLog(
  operationInput: OperationInput
): Promise<RecordPasswordChangeLogMutation | null | undefined> {
  try {
    const apolloClient = await generateApolloClient();
    const result: FetchResult<RecordPasswordChangeLogMutation> =
      await apolloClient.mutate<
        RecordPasswordChangeLogMutation,
        RecordPasswordChangeLogMutationVariables
      >({
        mutation: RecordPasswordChangeLogDocument,
        variables: {
          logInput: operationInput,
        },
      });
    return result.data;
  } catch (err: unknown) {
    console.error(err);
    if (err instanceof Error) {
      err.message = "パスワード変更ログの記録に失敗しました。";
      throw err;
    } else {
      throw new Error("パスワード変更ログの記録に失敗しました。（Unknown error）");
    }
  }
}

/**
 * オペレータ更新をする
 * @param cognitoUserUpdateInput オペレータ登録情報
 * @returns 更新結果
 */
export async function updateCognitoUser(
  cognitoUserUpdateInput: CognitoUserUpdateInput,
  logInput: OperationInput
): Promise<UpdateCognitoUserMutation | null | undefined> {
  try {
    //graphql接続
    const apolloClient = await generateApolloClient();
    const result: FetchResult<UpdateCognitoUserMutation> =
      await apolloClient.mutate<
        UpdateCognitoUserMutation,
        UpdateCognitoUserMutationVariables
      >({
        mutation: UpdateCognitoUserDocument,
        variables: {
          input: cognitoUserUpdateInput,
          logInput: logInput,
        },
      });
    return result.data;
  } catch (err: unknown) {
    console.error(err);
    if (err instanceof Error) {
      err.message = "オペレータ更新に失敗しました。";
      throw err;
    } else {
      throw new Error("オペレータ更新に失敗しました。（Unknown error）");
    }
  }
}

/**
 * オペレータのパスワードを更新する
 * @param cognitoUserPasswordUpdateInput オペレータパスワード情報
 * @returns 更新結果
 */
export async function updateCognitoUserPassword(
  cognitoUserPasswordUpdateInput: CognitoUserPasswordUpdateInput,
  logInput: OperationInput
): Promise<UpdateCognitoUserPasswordMutation | null | undefined> {
  try {
    //graphql接続
    const apolloClient = await generateApolloClient();
    const result: FetchResult<UpdateCognitoUserPasswordMutation> =
      await apolloClient.mutate<
        UpdateCognitoUserPasswordMutation,
        UpdateCognitoUserPasswordMutationVariables
      >({
        mutation: UpdateCognitoUserPasswordDocument,
        variables: {
          input: cognitoUserPasswordUpdateInput,
          logInput: logInput,
        },
      });
    return result.data;
  } catch (err: unknown) {
    console.error(err);
    if (err instanceof Error) {
      err.message = "オペレータパスワード更新に失敗しました。";
      throw err;
    } else {
      throw new Error(
        "オペレータパスワード更新に失敗しました。（Unknown error）"
      );
    }
  }
}

/**
 * オペレータ削除をする
 * @param cognitoUserCommonInput オペレータ登録情報
 * @returns 更新結果
 */
export async function deleteCognitoUser(
  cognitoUserCommonInput: CognitoUserCommonInput,
  operationInput: OperationInput
): Promise<UpdateCognitoUserMutation | null | undefined> {
  try {
    //graphql接続
    const apolloClient = await generateApolloClient();
    const result: FetchResult<DeleteCognitoUserMutation> =
      await apolloClient.mutate<
        DeleteCognitoUserMutation,
        DeleteCognitoUserMutationVariables
      >({
        mutation: DeleteCognitoUserDocument,
        variables: {
          input: cognitoUserCommonInput,
          logInput: operationInput,
        },
      });
    return result.data;
  } catch (err: unknown) {
    console.error(err);
    if (err instanceof Error) {
      err.message = "オペレータ削除に失敗しました。";
      throw err;
    } else {
      throw new Error("オペレータ削除に失敗しました。（Unknown error）");
    }
  }
}

/**
 * オペレータIDの重複件数を取得する
 * @param cognitoUserCountInput CognitoUserCountInput
 * @returns 重複件数
 */
export async function getCognitoUserCount(
  cognitoUserCountInput: CognitoUserCountInput
): Promise<number> {
  try {
    //graphql接続
    const apolloClient = await generateApolloClient();
    // Cogniteユーザに存在しているかチェック
    const countOperatorId: ApolloQueryResult<GetCognitoUserCountQuery> =
      await apolloClient.query<GetCognitoUserCountQuery>({
        query: GetCognitoUserCountDocument,
        variables: {
          input: cognitoUserCountInput,
        },
        fetchPolicy: "no-cache",
      });
    return countOperatorId.data.getCognitoUserCount!.count
      ? countOperatorId.data.getCognitoUserCount!.count
      : 0;
  } catch (err: unknown) {
    console.error(err);
    if (err instanceof Error) {
      err.message = "オペレータIDの重複確認に失敗しました。";
      throw err;
    } else {
      throw new Error(
        "オペレータIDの重複確認に失敗しました。（Unknown error）"
      );
    }
  }
}

/**
 * オペレータIDの重複件数を取得する
 * @param cognitoUserSameEmailCountInput CognitoUserSameEmailCountInput
 * @returns 重複件数
 */
export async function getCognitoUserSameEmailCount(
  cognitoUserSameEmailCountInput: CognitoUserSameEmailCountInput
): Promise<number> {
  try {
    //graphql接続
    const apolloClient = await generateApolloClient();
    // Cogniteメールに存在しているかチェック
    const countEmail: ApolloQueryResult<GetCognitoUserSameEmailCountQuery> =
      await apolloClient.query<GetCognitoUserSameEmailCountQuery>({
        query: GetCognitoUserSameEmailCountDocument,
        variables: {
          input: cognitoUserSameEmailCountInput,
        },
        fetchPolicy: "no-cache",
      });
    return countEmail.data.getCognitoUserSameEmailCount!.count
      ? countEmail.data.getCognitoUserSameEmailCount!.count
      : 0;
  } catch (err: unknown) {
    console.error(err);
    if (err instanceof Error) {
      err.message = "オペレータ認証E-メールの重複確認に失敗しました。";
      throw err;
    } else {
      throw new Error(
        "オペレータ認証E-メールの重複確認に失敗しました。（Unknown error）"
      );
    }
  }
}

/**
 * オペレータ情報を取得する
 * @param cognitoUserCommonInput CognitoUserCommonInput
 * @param operationInput OperationInput
 * @returns
 */
export async function getCognitoUser(
  cognitoUserCommonInput: CognitoUserCommonInput,
  operationInput: OperationInput
): Promise<
  | {
      __typename?: "CognitoUser" | undefined;
      customer_code: string;
      chain_code: string;
      auth_type: string;
      email?: string | null | undefined;
      group: string;
      group_name: string;
      menu_permission: string;
      name: string;
      personal_info: string;
      personal_info_desc: string;
      personal_info_dl: string;
      personal_info_dl_desc: string;
      phone_number?: string | null | undefined;
      user_id: string;
      shop_code?: string | null | undefined;
    }
  | null
  | undefined
> {
  try {
    //graphql接続
    const apolloClient = await generateApolloClient();
    // Cogniteユーザに存在しているかチェック
    const operator: ApolloQueryResult<GetCognitoUserQuery> =
      await apolloClient.query<GetCognitoUserQuery>({
        query: GetCognitoUserDocument,
        variables: {
          input: cognitoUserCommonInput,
          logInput: operationInput,
        },
        fetchPolicy: "no-cache",
      });
    return operator.data.getCognitoUser;
  } catch (err: unknown) {
    console.error(err);
    if (err instanceof Error) {
      err.message = "オペレータ情報の取得に失敗しました。";
      throw err;
    } else {
      throw new Error("オペレータ情報の取得に失敗しました。（Unknown error）");
    }
  }
}

/**
 * オペレータ一覧を取得する
 * @param listInput CognitoUserListInput
 * @param operationInput OperationInput
 * @param countInput CognitoUserCountInput
 * @returns オペレータ一覧を返す
 */
export async function listCognitoUser(
  listInput: CognitoUserListInput,
  operationInput: OperationInput,
  countInput: CognitoUserCountInput
): Promise<ListCognitoUserQuery> {
  try {
    //graphql接続
    const apolloClient = await generateApolloClient();
    const listCognitoUser = await apolloClient.query<
      ListCognitoUserQuery,
      ListCognitoUserQueryVariables
    >({
      query: ListCognitoUserDocument,
      variables: {
        listInput: listInput,
        logInput: operationInput,
        countInput: countInput,
      },
      fetchPolicy: "no-cache",
    });

    return listCognitoUser.data;
  } catch (err: unknown) {
    console.error(err);
    if (err instanceof Error) {
      err.message = "オペレータ一覧データの取得に失敗しました。";
      throw err;
    } else {
      throw new Error(
        "オペレータ一覧データの取得に失敗しました。（Unknown error）"
      );
    }
  }
}
